<template>
    <div class="flex flex-col">
        <template v-if="isSetupIntent">
            <stripe-setup-form
                ref="stripeFormRef"
                :key="stripeFormKey"
                :checkout-id="checkoutId"
                :payment-source-id="paymentSourceId"
                @payment-method="handleTokenPaymentMethod"
                @display-stripe-error="displayStripeError"
            />
        </template>

        <stripe-payment-form
            v-else
            ref="stripeFormRef"
            :key="stripeFormKey"
            :amount="amount"
            :checkout-id="checkoutId"
            :payment-source-id="paymentSourceId"
            @payment-method="handleTokenPaymentMethod"
            @display-stripe-error="displayStripeError"
        />

        <div class="flex justify-end">
            <prime-button
                text
                class="!p-1"
                :label="t('payments.refreshPaymentForm')"
                :disabled="isProcessing"
                @click="refreshStripeForm"
            />
        </div>

        <div
            v-if="stripeMessage.length > 0"
            class="mt-5 text-lg font-bold text-danger-400"
        >
            {{ stripeMessage }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import PrimeButton from 'primevue/button'
import { useI18n } from 'vue-i18n'
import StripePaymentForm from '~/components/Payments/StripePaymentForm.vue'
import StripeSetupForm from '~/components/Payments/StripeSetupForm.vue'
import type { StripeError } from '@stripe/stripe-js'
import { useTimeoutFn } from '@vueuse/core'
import type { TokenPaymentMethod } from '~/components/Checkout/Sections/Payment/payment-method'

const props = defineProps<{
    paymentSourceId: string
    checkoutId?: string
    amount?: string
    isProcessing?: boolean
}>()

const emit = defineEmits<{
    (e: 'paymentMethod', payload: TokenPaymentMethod | undefined): void
}>()

const { t } = useI18n({ useScope: 'global' })

const isSetupIntent = computed(() => props.amount === undefined)
const keyCounter = ref(1)
const stripeMessage = ref('')
const stripeFormKey = computed(() => {
    // These inputs define the Intent owned by the mounted Stripe Element.
    return `${props.paymentSourceId}:${props.checkoutId ?? 'profile'}:${props.amount ?? 'setup'}:${keyCounter.value}`
})

function handleTokenPaymentMethod(
    paymentMethod: TokenPaymentMethod | undefined
): void {
    emit('paymentMethod', paymentMethod)
}

function refreshStripeForm() {
    // A replacement Element cannot reuse a token emitted by the Element being removed.
    emit('paymentMethod', undefined)
    stripeMessage.value = ''
    keyCounter.value++
}

//--------------------------------------------
// display stripe errors
//--------------------------------------------

function displayStripeError(error: StripeError) {
    //
    // fail display error
    //
    if (error.type === 'card_error' || error.type === 'validation_error') {
        stripeMessage.value = error.message as string
        if (stripeMessage.value.length == 0) {
            stripeMessage.value = t('payments.unexpectedError')
        }
    } else {
        stripeMessage.value = t('payments.unexpectedError')
    }

    if (import.meta.env.DEV) {
        stripeMessage.value +=
            '          (DEV Mode: ' + error.type + ') ' + error.message
    }
    useTimeoutFn(() => (stripeMessage.value = ''), 6000)
}

const stripeFormRef =
    ref<InstanceType<typeof StripePaymentForm | typeof StripeSetupForm>>(null)
async function submitStripeForm(): Promise<boolean> {
    if (stripeFormRef.value === null) {
        // A keyed remount can briefly clear the child reference before its replacement mounts.
        return false
    }

    // A previous confirmation result must not survive a failed confirmation attempt.
    emit('paymentMethod', undefined)
    // The union ref can retain a legacy void return while either child remounts.
    return (await stripeFormRef.value.submitStripeElementsForm()) === true
}
defineExpose({
    submitStripeForm,
    refreshStripeForm
})
</script>
